import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { calculateBookingFinancials } from '../utils/financialCalculations';

const BRAND_BLUE = [0, 71, 255];
const DARK_NAVY = [14, 27, 43];

export const financialReportService = {
  prepareDetailedRows(bookings) {
    return bookings
      .filter(b => b.status === 'completed')
      .map(b => {
        const f = calculateBookingFinancials(b.total_price);
        return {
          id: b.id.slice(0, 8).toUpperCase(),
          date: b.booking_date,
          barber: b.barber?.full_name || 'N/A',
          customer: b.customer?.full_name || 'N/A',
          service: b.service?.name || 'N/A',
          gross: f.grossAmount,
          transport: f.transportFee,
          netRevenue: f.netRevenue,
          serviceCharge: f.serviceCharge,
          distributable: f.distributable,
          barberShare: f.barberShare,
          ownerCommission: f.ownerShare,
          totalBarberEarnings: f.totalBarberEarnings,
          paymentStatus: b.payment_status || 'pending',
        };
      });
  },

  exportToPDF(bookings, summary, barberBreakdown, title, fileName) {
    const doc = new jsPDF({ orientation: 'landscape' });
    const rows = this.prepareDetailedRows(bookings);

    // Header
    doc.setFillColor(...DARK_NAVY);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('BookOTG', 14, 12);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 14, 20);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()} | Managed by Fesy Marketing Pvt Ltd`, 14, 27);

    // Summary Box
    doc.setTextColor(...DARK_NAVY);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Summary', 14, 42);

    const summaryData = [
      ['Gross Revenue', `Rs. ${summary.grossRevenue.toLocaleString()}`],
      ['Total Transport Fees (to barbers)', `Rs. ${summary.totalTransportFees.toLocaleString()}`],
      ['Service Charges (4% hosting)', `Rs. ${summary.totalServiceCharges.toLocaleString()}`],
      ['Total Distributable', `Rs. ${summary.totalDistributable.toLocaleString()}`],
      ['Total Barber Payouts (Transport + 80%)', `Rs. ${summary.totalBarberEarnings.toLocaleString()}`],
      ["Fernando's Commission (20%)", `Rs. ${summary.totalOwnerCommission.toLocaleString()}`],
    ];

    doc.autoTable({
      startY: 46,
      head: [['Metric', 'Amount']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: BRAND_BLUE, textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
      margin: { left: 14, right: 14 },
      tableWidth: 120,
    });

    // Barber Breakdown
    const afterSummary = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Barber Payout Breakdown', 14, afterSummary);

    doc.autoTable({
      startY: afterSummary + 4,
      head: [['Barber', 'Bookings', 'Gross (Rs.)', 'Transport (Rs.)', 'Service Charge (Rs.)', 'Barber Share (Rs.)', 'Total Payout (Rs.)', 'Owner Commission (Rs.)']],
      body: barberBreakdown.map(b => [
        b.barberName,
        b.bookingCount,
        b.grossRevenue.toLocaleString(),
        b.transportFees.toLocaleString(),
        b.serviceCharges.toLocaleString(),
        b.barberShare.toLocaleString(),
        b.totalBarberEarnings.toLocaleString(),
        b.ownerCommission.toLocaleString(),
      ]),
      theme: 'striped',
      headStyles: { fillColor: DARK_NAVY, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      margin: { left: 14, right: 14 },
    });

    // Transaction Detail
    const afterBarber = doc.lastAutoTable.finalY + 10;
    doc.addPage();
    doc.setFillColor(...DARK_NAVY);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Transaction Ledger', 14, 11);

    doc.autoTable({
      startY: 20,
      head: [['Ref', 'Date', 'Barber', 'Service', 'Gross', 'Transport', 'Net Rev.', 'Svc Chrg', 'Dist.', 'Barber Share', 'Total Payout', 'Owner (20%)', 'Payment']],
      body: rows.map(r => [
        r.id,
        r.date,
        r.barber,
        r.service,
        `Rs.${r.gross.toLocaleString()}`,
        `Rs.${r.transport.toLocaleString()}`,
        `Rs.${r.netRevenue.toLocaleString()}`,
        `Rs.${r.serviceCharge.toLocaleString()}`,
        `Rs.${r.distributable.toLocaleString()}`,
        `Rs.${r.barberShare.toLocaleString()}`,
        `Rs.${r.totalBarberEarnings.toLocaleString()}`,
        `Rs.${r.ownerCommission.toLocaleString()}`,
        r.paymentStatus.toUpperCase(),
      ]),
      theme: 'striped',
      headStyles: { fillColor: BRAND_BLUE, textColor: 255, fontStyle: 'bold', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      margin: { left: 8, right: 8 },
    });

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${i} of ${pageCount} — BookOTG Financial Report — Confidential`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 8,
        { align: 'center' }
      );
    }

    doc.save(`${fileName}.pdf`);
  },

  exportToExcel(bookings, summary, barberBreakdown, fileName) {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summarySheet = XLSX.utils.aoa_to_sheet([
      ['BookOTG Financial Report'],
      ['Generated:', new Date().toLocaleString()],
      [],
      ['FINANCIAL SUMMARY'],
      ['Metric', 'Amount (LKR)'],
      ['Gross Revenue', summary.grossRevenue],
      ['Total Transport Fees (to barbers)', summary.totalTransportFees],
      ['Service Charges (4% hosting)', summary.totalServiceCharges],
      ['Total Distributable', summary.totalDistributable],
      ['Total Barber Payouts (Transport + 80%)', summary.totalBarberEarnings],
      ["Fernando's Commission (20%)", summary.totalOwnerCommission],
      ['Completed Bookings', summary.completedCount],
    ]);
    summarySheet['!cols'] = [{ wch: 40 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Sheet 2: Barber Breakdown
    const barberSheet = XLSX.utils.aoa_to_sheet([
      ['Barber', 'Bookings', 'Gross Revenue', 'Transport Fees', 'Service Charges', 'Barber Share (80%)', 'Total Barber Payout', 'Owner Commission (20%)'],
      ...barberBreakdown.map(b => [
        b.barberName,
        b.bookingCount,
        b.grossRevenue,
        b.transportFees,
        b.serviceCharges,
        b.barberShare,
        b.totalBarberEarnings,
        b.ownerCommission,
      ]),
    ]);
    barberSheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 22 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, barberSheet, 'Barber Payouts');

    // Sheet 3: Full Transaction Ledger
    const rows = this.prepareDetailedRows(bookings);
    const ledgerSheet = XLSX.utils.aoa_to_sheet([
      ['Ref ID', 'Date', 'Barber', 'Customer', 'Service', 'Gross (LKR)', 'Transport (LKR)', 'Net Revenue', 'Service Charge (4%)', 'Distributable', 'Barber Share (80%)', 'Total Barber Payout', 'Owner Commission (20%)', 'Payment Status'],
      ...rows.map(r => [
        r.id,
        r.date,
        r.barber,
        r.customer,
        r.service,
        r.gross,
        r.transport,
        r.netRevenue,
        r.serviceCharge,
        r.distributable,
        r.barberShare,
        r.totalBarberEarnings,
        r.ownerCommission,
        r.paymentStatus,
      ]),
    ]);
    ledgerSheet['!cols'] = [
      { wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 22 },
      { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 14 },
      { wch: 18 }, { wch: 20 }, { wch: 22 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, ledgerSheet, 'Transaction Ledger');

    XLSX.writeFile(wb, `${fileName}.xlsx`);
  },
};